import { EXTENSION, dataOf, importDocument, nodeTree, serializeDocument, validateDocument } from "./document.js";
import { readGlb, writeGlb } from "./glb.js";
import { resolveModelVistaNodePath } from "../logic/modelvista_extension.js";

const copy = value => structuredClone(value);
const arrays = ["nodes", "meshes", "materials", "accessors", "bufferViews", "images", "textures", "samplers", "skins", "cameras", "animations"];
const pathKeys = new Set(["Path", "ContainerPath", "RendererPath", "DummyPath", "TargetTransformPath", "ReferenceTransformPath", "TargetCameraPath", "TargetAnimationPath"]);

function uniqueName(name, used) {
    const base = String(name || "Model").replaceAll("/", "_");
    let result = base, suffix = 2;
    while (used.has(result) || used.has(result.replace(/\.\d{3}$/, ""))) result = `${base} (${suffix++})`;
    used.add(result); return result;
}

function primaryBuffer(source, json) {
    const buffer = json.buffers?.[0];
    if (!buffer) return new Uint8Array();
    let bytes = source.binaryChunk;
    if (buffer.uri) {
        const comma = buffer.uri.indexOf(","), header = buffer.uri.slice(0, comma), payload = buffer.uri.slice(comma + 1);
        bytes = /;base64$/i.test(header) ? Uint8Array.from(atob(payload), char => char.charCodeAt(0))
            : Uint8Array.from(payload.match(/%[\da-f]{2}|[\s\S]/gi) ?? [], char => char.startsWith("%") ? parseInt(char.slice(1), 16) : char.charCodeAt(0));
    }
    if (bytes.length < buffer.byteLength) throw new Error("The model's embedded buffer is incomplete.");
    return bytes.slice(0, buffer.byteLength);
}

function remapTextures(value, offset) {
    if (!value || typeof value !== "object") return;
    for (const [key, item] of Object.entries(value)) {
        if (key.endsWith("Texture") && Number.isInteger(item?.index)) item.index += offset;
        else remapTextures(item, offset);
    }
}

function metadata(value, maps, relative = false) {
    if (!value || typeof value !== "object") return;
    for (const [key, item] of Object.entries(value)) {
        if (["TriggerAction", "HoldAction"].includes(key)) continue;
        if (key === "LegacyClips") { metadata(item, maps, true); continue; }
        if (!relative && maps.path && pathKeys.has(key) && typeof item === "string") {
            // Empty optional paths mean "use the container/default", rather
            // than an explicit reference to the asset root.
            if (item || !["DummyPath", "TargetAnimationPath", "TargetCameraPath"].includes(key)) value[key] = maps.path(item);
        } else if (!relative && maps.path && ["TargetPaths", "TargetLightPaths", "MaterialDummyPaths"].includes(key) && Array.isArray(item)) value[key] = item.map(maps.path);
        else if (maps.image && key.endsWith("ImageIndex") && Number.isInteger(item) && item >= 0) value[key] = maps.image(item);
        else if (maps.image && key.endsWith("ImageIndices") && Array.isArray(item)) value[key] = item.map(maps.image);
        else if (maps.node && key === "NodeIndex" && Number.isInteger(item) && item >= 0) value[key] = maps.node(item);
        else if (maps.bufferView && key === "PayloadBufferViewIndex" && Number.isInteger(item) && item >= 0) value[key] = maps.bufferView(item);
        else if (maps.materialId && key === "MaterialId" && Number.isInteger(item)) value[key] = maps.materialId(item);
        else if (maps.materialId && key === "MaterialIds" && Array.isArray(item)) value[key] = item.map(maps.materialId);
        else if (maps.materialName && key === "MaterialName" && typeof item === "string") value[key] = maps.materialName(item);
        else metadata(item, maps, relative);
    }
}

function preserveMaterialSchemas(extension) {
    const version = Number(extension.SchemaVersion ?? 1);
    for (const record of [...(extension.Materials ?? []), ...(extension.MaterialBindings ?? [])]) {
        if (record.SourceSchemaVersion === undefined) record.SourceSchemaVersion = version;
    }
}

// Legacy clip indices must sit after all native clips in the combined asset.
// Null slots preserve those indices without manufacturing animation curves.
function remapAnimationStates(sets, originalNativeCount, totalNativeCount, nativeOffset) {
    for (const set of sets.filter(set => set.kind === "Animation")) {
        const data = dataOf(set), legacy = data.LegacyClips ?? [];
        const legacyIndices = new Map(), clips = [];
        legacy.forEach((clip, index) => { if (clip) { legacyIndices.set(index, totalNativeCount + clips.length); clips.push(clip); } });
        if (legacy.length) data.LegacyClips = [...Array(totalNativeCount).fill(null), ...clips];
        for (const option of set.options) {
            const index = option.raw.AnimationIndex ?? -1;
            if (index >= 0) option.raw.AnimationIndex = index < originalNativeCount ? nativeOffset + index : legacyIndices.get(index) ?? -1;
        }
    }
}

export function insertModel(doc, sourceBytes, incomingBytes, filename, parentIndex, importReport = null) {
    const parent = nodeTree(doc.json).find(node => node.index === parentIndex);
    if (!parent || parent.path.split("/").some(name => name.startsWith("__INNOWORKS_MATERIAL"))) throw new Error("Select one part to insert a model beneath it.");
    const source = readGlb(sourceBytes), assetSource = readGlb(incomingBytes), asset = importDocument(incomingBytes, filename);
    const problems = validateDocument(asset);
    if (problems.errors.length) throw new Error(problems.errors.join("\n"));
    for (const extension of Object.keys(asset.json.extensions ?? {})) {
        if (![EXTENSION, "KHR_lights_punctual", "KHR_materials_variants"].includes(extension)) throw new Error(`This model's root extension cannot be combined yet: ${extension}`);
    }
    const next = copy(doc), json = next.json, imported = copy(asset.json), assetTree = nodeTree(asset.json), assetRoot = assetTree[0].index;
    const offsets = Object.fromEntries(arrays.map(key => [key, (json[key] ?? []).length]));
    const baseExtension = json.extensions[EXTENSION], incomingExtension = imported.extensions[EXTENSION];
    preserveMaterialSchemas(baseExtension); preserveMaterialSchemas(incomingExtension);
    const baseNativeCount = offsets.animations, incomingNativeCount = (imported.animations ?? []).length, totalNativeCount = baseNativeCount + incomingNativeCount;
    remapAnimationStates(next.sets, baseNativeCount, totalNativeCount, 0);
    remapAnimationStates(asset.sets, incomingNativeCount, totalNativeCount, baseNativeCount);

    const baseBuffer = primaryBuffer(source, json), addedBuffer = primaryBuffer(assetSource, imported);
    const binaryOffset = Math.ceil(baseBuffer.length / 4) * 4, binary = new Uint8Array(binaryOffset + addedBuffer.length);
    binary.set(baseBuffer); binary.set(addedBuffer, binaryOffset);
    if (!json.buffers?.length) json.buffers = [{ byteLength: 0 }];
    const extraBufferOffset = json.buffers.length - 1;
    for (const buffer of (imported.buffers ?? []).slice(1)) {
        if (!buffer.uri) throw new Error("The model contains an unsupported secondary binary buffer.");
        json.buffers.push(buffer);
    }
    json.buffers[0].byteLength = binary.length; delete json.buffers[0].uri;
    const remapBuffer = (value, offsetKey = "byteOffset") => {
        if (!imported.buffers?.[value.buffer]) throw new Error("The model references a missing embedded buffer.");
        if (value.buffer === 0) { value.buffer = 0; value[offsetKey] = (value[offsetKey] ?? 0) + binaryOffset; }
        else value.buffer += extraBufferOffset;
    };
    for (const view of imported.bufferViews ?? []) {
        remapBuffer(view);
        if (view.extensions?.EXT_meshopt_compression) remapBuffer(view.extensions.EXT_meshopt_compression);
    }
    for (const accessor of imported.accessors ?? []) {
        if (accessor.bufferView !== undefined) accessor.bufferView += offsets.bufferViews;
        if (accessor.sparse) { accessor.sparse.indices.bufferView += offsets.bufferViews; accessor.sparse.values.bufferView += offsets.bufferViews; }
    }
    for (const image of imported.images ?? []) if (image.bufferView !== undefined) image.bufferView += offsets.bufferViews;
    for (const texture of imported.textures ?? []) {
        if (texture.sampler !== undefined) texture.sampler += offsets.samplers;
        if (texture.source !== undefined) texture.source += offsets.images;
        for (const extension of ["KHR_texture_basisu", "EXT_texture_webp", "EXT_texture_avif", "MSFT_texture_dds"]) {
            if (texture.extensions?.[extension]?.source !== undefined) texture.extensions[extension].source += offsets.images;
        }
    }
    const materialNames = new Set([...(json.materials ?? []).map(material => material.name), ...(baseExtension.Materials ?? []).map(material => material.Name)]);
    const names = new Map();
    const materialName = name => {
        if (!name) return name;
        if (!names.has(name)) names.set(name, uniqueName(name, materialNames));
        return names.get(name);
    };
    for (const material of imported.materials ?? []) { material.name = materialName(material.name); remapTextures(material, offsets.textures); }
    const materialIds = new Map(); let nextId = Math.max(-1, ...(baseExtension.Materials ?? []).map(record => record.Id ?? -1)) + 1;
    for (const record of incomingExtension.Materials ?? []) {
        materialIds.set(record.Id, nextId++); record.Name = materialName(record.Name);
    }
    const variantsOffset = json.extensions.KHR_materials_variants?.variants?.length ?? 0;
    for (const mesh of imported.meshes ?? []) for (const primitive of mesh.primitives ?? []) {
        for (const key of Object.keys(primitive.attributes ?? {})) primitive.attributes[key] += offsets.accessors;
        if (primitive.indices !== undefined) primitive.indices += offsets.accessors;
        if (primitive.material !== undefined) primitive.material += offsets.materials;
        for (const target of primitive.targets ?? []) for (const key of Object.keys(target)) target[key] += offsets.accessors;
        if (primitive.extensions?.KHR_draco_mesh_compression) primitive.extensions.KHR_draco_mesh_compression.bufferView += offsets.bufferViews;
        for (const mapping of primitive.extensions?.KHR_materials_variants?.mappings ?? []) {
            mapping.material += offsets.materials; mapping.variants = mapping.variants.map(index => index + variantsOffset);
        }
    }
    const lightsOffset = json.extensions.KHR_lights_punctual?.lights?.length ?? 0;
    for (const node of imported.nodes) {
        if (node.children) node.children = node.children.map(index => index + offsets.nodes);
        for (const [key, array] of [["mesh", "meshes"], ["skin", "skins"], ["camera", "cameras"]]) if (node[key] !== undefined) node[key] += offsets[array];
        if (node.extensions?.KHR_lights_punctual) node.extensions.KHR_lights_punctual.light += lightsOffset;
        const attributes = node.extensions?.EXT_mesh_gpu_instancing?.attributes;
        for (const key of Object.keys(attributes ?? {})) attributes[key] += offsets.accessors;
        if (node.extensions?.MSFT_lod?.ids) node.extensions.MSFT_lod.ids = node.extensions.MSFT_lod.ids.map(index => index + offsets.nodes);
    }
    for (const skin of imported.skins ?? []) {
        skin.joints = skin.joints.map(index => index + offsets.nodes);
        if (skin.skeleton !== undefined) skin.skeleton += offsets.nodes;
        if (skin.inverseBindMatrices !== undefined) skin.inverseBindMatrices += offsets.accessors;
    }
    for (const animation of imported.animations ?? []) {
        for (const sampler of animation.samplers) { sampler.input += offsets.accessors; sampler.output += offsets.accessors; }
        for (const channel of animation.channels) {
            if (channel.target.node !== undefined) channel.target.node += offsets.nodes;
            const pointer = channel.target.extensions?.KHR_animation_pointer;
            if (pointer) pointer.pointer = pointer.pointer.replace(/^\/(\w+)\/(\d+)/, (match, key, index) => offsets[key] === undefined ? match : `/${key}/${Number(index) + offsets[key]}`)
                .replace(/^\/extensions\/KHR_lights_punctual\/lights\/(\d+)/, (_, index) => `/extensions/KHR_lights_punctual/lights/${Number(index) + lightsOffset}`);
        }
    }
    const siblingNames = new Set((json.nodes[parentIndex].children ?? []).flatMap(index => { const name = json.nodes[index].name || ""; return [name, name.replace(/\.\d{3}$/, "")]; }));
    imported.nodes[assetRoot].name = uniqueName(filename.replace(/\.(glb|fbx)$/i, ""), siblingNames);
    const insertedRoot = offsets.nodes + assetRoot;
    for (const key of arrays) if (imported[key]?.length) json[key] = [...(json[key] ?? []), ...imported[key]];
    json.nodes[parentIndex].children = [...(json.nodes[parentIndex].children ?? []), insertedRoot];
    const paths = new Map(nodeTree(json).map(node => [node.index, node.path]));
    const path = value => {
        const index = resolveModelVistaNodePath(asset.json, assetRoot, value);
        if (index === null) return [paths.get(insertedRoot), value].filter(Boolean).join("/");
        return paths.get(index + offsets.nodes);
    };
    // Extension image IDs can also address payloads beyond glTF's images array.
    // Reallocate those IDs after both sets of standard images, without padding
    // the glTF array or changing which images are considered extension-only.
    let nextImage = offsets.images + (imported.images ?? []).length;
    const oldImages = new Map(), addedImages = new Map();
    const imageMap = (index, count, offset, allocated) => {
        if (index < 0) return index;
        if (index < count) return index + offset;
        if (!allocated.has(index)) allocated.set(index, nextImage++);
        return allocated.get(index);
    };
    const baseImage = index => imageMap(index, offsets.images, 0, oldImages);
    metadata(baseExtension, { image: baseImage }); metadata(next.sets, { image: baseImage });
    const maps = { path, image: index => imageMap(index, (imported.images ?? []).length, offsets.images, addedImages), node: index => index + offsets.nodes,
        bufferView: index => index + offsets.bufferViews, materialId: id => materialIds.get(id) ?? id, materialName };
    metadata(incomingExtension, maps); metadata(asset.sets, maps);
    for (const record of incomingExtension.Materials ?? []) record.Id = materialIds.get(record.Id);
    for (const [key, value] of Object.entries(incomingExtension)) {
        if (["RootName", "SchemaVersion", "SourceRenderPipeline", "Containers", "AssetThumbnailImageIndex", "AssetThumbnailBase64"].includes(key)) continue;
        if (Array.isArray(value)) baseExtension[key] = [...(baseExtension[key] ?? []), ...value];
        else if (baseExtension[key] === undefined) baseExtension[key] = value;
    }
    baseExtension.SchemaVersion = Math.max(Number(baseExtension.SchemaVersion ?? 1), Number(incomingExtension.SchemaVersion ?? 1));
    for (const [extension, key] of [["KHR_lights_punctual", "lights"], ["KHR_materials_variants", "variants"]]) if (imported.extensions[extension]) {
        json.extensions[extension] = { ...(json.extensions[extension] ?? {}), [key]: [...(json.extensions[extension]?.[key] ?? []), ...imported.extensions[extension][key]] };
    }
    for (const key of ["extensionsUsed", "extensionsRequired"]) {
        const values = [...new Set([...(json[key] ?? []), ...(imported[key] ?? [])])];
        if (values.length) json[key] = values;
    }
    next.sets.push(...asset.sets);
    next.deferredStateIds = [...(next.deferredStateIds ?? []), ...asset.sets.map(set => set.id)];
    next.insertedModels = [...(next.insertedModels ?? []), { filename, rootIndex: insertedRoot, importReport: importReport ? copy(importReport) : null }];
    const issues = validateDocument(next);
    if (issues.errors.length) throw new Error(issues.errors.join("\n"));
    const bytes = writeGlb(source, serializeDocument(next), binary);
    Object.assign(doc, next);
    return { sourceBytes: bytes, selectedNodes: [insertedRoot] };
}
